﻿import * as $ from "jquery";
import { getSupportedLocale, DefaultLocale } from "./LocalizationSupport";

/** Represents the contents of a label file. */
export interface MessageFile {

    /** Finds a resolved label keyed by the label key. */
    [labelKey: string]: string;
}

/** The mechanism used to resolve labels. */
export class LabelResolver {

    /** The application's current default locale. */
    public CurrentLocale: string;

    /** An object containing retrieved labels. */
    private Labels: { [locale: string]: MessageFile };

    constructor() {
        this.CurrentLocale = DefaultLocale;
        this.Labels = {};
    }

    /**
     * Adds labels for the given locale.
     * @param locale The locale for which to add labels.
     * @param labels The labels to add.
     */
    public addLabels(locale: string, labels: MessageFile): void {
        if (this.Labels[locale]) {
            $.extend(true, this.Labels[locale], labels);
        } else {
            this.Labels[locale] = labels;
        }
    }

    /**
     * Gets the locales that are currently loaded.
     * @returns The locales that are currently loaded.
     */
    public getLoadedLocales(): string[] {
        return Object.keys(this.Labels);
    }

    /**
     * Resolves a label based on the provided label key and locale.
     * @param labelKey The key of the label to resolve.
     * @param locale The locale to resolve the label in. If not provided, the current culture is used.
     * @returns The resolved label
     */
    public resolveLabel(labelKey: string, locale: string = this.CurrentLocale): string {
        let currentLocaleLabels = this.getLocale(locale);
        if (currentLocaleLabels === undefined || !(labelKey in currentLocaleLabels)) {
            currentLocaleLabels = this.getLocale(DefaultLocale);
            if (currentLocaleLabels === undefined || !(labelKey in currentLocaleLabels)) {
                return labelKey;
            }
        }
        return currentLocaleLabels[labelKey];
    }

    /**
     * Gets the set of labels for a provided locale.
     * @param locale The locale to retrieve labels for.
     * @returns The list of labels for the provided locale
     */
    private getLocale(locale: string): MessageFile {
        return this.Labels[getSupportedLocale(locale)];
    }
}
